import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { ShieldCheck, Zap, FileCheck2, Loader2 } from 'lucide-react';

const LOGO_DARK = 'https://customer-assets.emergentagent.com/job_budget-gen-3/artifacts/pua56wyb_logo%20cauratech%20fondo%20oscuro.png';

export default function RegisterPage() {
  const { register, loading } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    setBusy(true);
    try {
      await register(email, password, name);
      toast.success('Cuenta creada. ¡Bienvenido!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo registrar');
    } finally { setBusy(false); }
  };

  return (
    <div className="relative z-10 grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <div className="relative hidden overflow-hidden lg:flex lg:flex-col lg:justify-between lg:p-12">
        <div className="absolute inset-0 tech-grid opacity-50" />
        <div className="absolute -left-32 -top-32 h-[420px] w-[420px] rounded-full bg-[radial-gradient(circle,rgba(85,117,245,0.25),transparent_60%)]" />
        <div className="absolute -bottom-40 -right-24 h-[420px] w-[420px] rounded-full bg-[radial-gradient(circle,rgba(76,201,240,0.18),transparent_60%)]" />
        <div className="relative">
          <img src={LOGO_DARK} alt="Cuaratech" className="h-16 w-auto" />
        </div>
        <div className="relative">
          <h2 className="font-display text-4xl font-semibold leading-tight tracking-tight xl:text-5xl">
            Crea tu cuenta y empieza a cotizar en minutos.
          </h2>
          <p className="mt-4 max-w-md text-base text-muted-foreground">
            Configura tu catálogo, tus clientes y emite presupuestos verificables al instante.
          </p>
          <ul className="mt-8 space-y-3 text-sm">
            <li className="flex items-center gap-3"><Zap className="h-4 w-4 text-[hsl(var(--primary))]" /> Catálogo personalizable</li>
            <li className="flex items-center gap-3"><FileCheck2 className="h-4 w-4 text-[hsl(var(--primary))]" /> Vigencia 15 días, IVA 16% (editable)</li>
            <li className="flex items-center gap-3"><ShieldCheck className="h-4 w-4 text-[hsl(var(--primary))]" /> QR de seguridad para tus clientes</li>
          </ul>
        </div>
        <div className="relative text-xs text-muted-foreground">© {new Date().getFullYear()} Cuaratech</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="mb-6 flex flex-col items-center lg:hidden">
            <img src={LOGO_DARK} alt="Cuaratech" className="h-12 w-auto" />
          </div>
          <Card className="glass border-[var(--glass-border)] bg-[var(--glass)]">
            <CardContent className="p-7">
              <h1 className="font-display text-2xl font-semibold">Crear cuenta</h1>
              <p className="mt-1 text-sm text-muted-foreground">Registra al primer usuario administrador.</p>
              <form className="mt-6 space-y-4" onSubmit={onSubmit}>
                <div className="space-y-1.5">
                  <Label htmlFor="name">Nombre completo</Label>
                  <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="María Pérez" data-testid="register-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@cuaratech.com" data-testid="register-email-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">Contraseña</Label>
                  <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mínimo 6 caracteres" data-testid="register-password-input" />
                </div>
                <Button type="submit" className="btn-primary h-11 w-full" disabled={busy || loading} data-testid="register-form-submit-button">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Crear cuenta'}
                </Button>
              </form>
              <p className="mt-5 text-center text-sm text-muted-foreground">
                ¿Ya tienes cuenta?{' '}
                <Link to="/login" className="font-medium text-[hsl(var(--primary))] hover:underline" data-testid="register-go-login-link">
                  Inicia sesión
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
